import styled from 'styled-components';

export const PreviewContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
	align-items: center;
	justify-content: center;
	min-height: 60px;
`;

export const ColorSwatch = styled.div<{ color: string }>`
	width: 100%;
	height: 60px;
	background-color: ${({ color }) => color};
	border-radius: 4px;
	border: 1px solid ${({ theme }) => theme.gray[200]};
`;

export const LengthBar = styled.div<{ width: number }>`
	width: ${({ width }) => Math.min(width, 180)}px;
	height: 8px;
	background-color: ${({ theme }) => theme.gray[400]};
	border-radius: 4px;
`;

export const ShadowBox = styled.div<{ shadow: string }>`
	width: 60px;
	height: 60px;
	background-color: ${({ theme }) => theme.gray[0]};
	border-radius: 4px;
	box-shadow: ${({ shadow }) => shadow};
`;

export const FontSample = styled.div<{ fontFamily: string }>`
	font-size: 24px;
	font-weight: 600;
	font-family: ${({ fontFamily }) => fontFamily};
	color: ${({ theme }) => theme.gray[1000]};
`;

export const SizeSample = styled.div<{ fontSize: number }>`
	font-size: ${({ fontSize }) => fontSize}px;
	color: ${({ theme }) => theme.gray[1000]};
	line-height: 1.2;
`;

export const LineHeightSample = styled.div<{ lineHeight: number }>`
	font-size: 14px;
	line-height: ${({ lineHeight }) => lineHeight};
	color: ${({ theme }) => theme.gray[1000]};
	width: 100%;
`;

export const WeightSample = styled.div<{ fontWeight: number }>`
	font-size: 16px;
	font-weight: ${({ fontWeight }) => fontWeight};
	color: ${({ theme }) => theme.gray[1000]};
`;

export const RadiusBox = styled.div<{ borderRadius: number }>`
	width: 60px;
	height: 60px;
	background-color: ${({ theme }) => theme.gray[400]};
	border-radius: ${({ borderRadius }) => borderRadius}px;
`;

export const OpacityBox = styled.div<{ opacity: number }>`
	width: 60px;
	height: 60px;
	background: linear-gradient(45deg, ${({ theme }) => theme.gray[200]} 25%, transparent 25%),
		linear-gradient(-45deg, ${({ theme }) => theme.gray[200]} 25%, transparent 25%),
		linear-gradient(45deg, transparent 75%, ${({ theme }) => theme.gray[200]} 75%),
		linear-gradient(-45deg, transparent 75%, ${({ theme }) => theme.gray[200]} 75%);
	background-size: 8px 8px;
	background-position: 0 0, 0 4px, 4px -4px, -4px 0px;
	border-radius: 4px;
	position: relative;
`;

export const OpacityOverlay = styled.div<{ opacity: number }>`
	width: 100%;
	height: 100%;
	background-color: ${({ theme }) => theme.gray[1000]};
	opacity: ${({ opacity }) => opacity};
	border-radius: 4px;
`;

export const TransitionBox = styled.div<{ duration: string; timingFunction: string }>`
	width: 60px;
	height: 60px;
	background-color: ${({ theme }) => theme.gray[400]};
	border-radius: 4px;
	transition: transform ${({ duration }) => duration} ${({ timingFunction }) => timingFunction};

	&:hover {
		transform: scale(1.1);
	}
`;

export const TokenCard = styled.div<{ $selected?: boolean }>`
	background-color: ${({ theme }) => theme.gray[0]};
	border: 1px solid ${({ theme, $selected }) => ($selected ? theme.blue[500] : theme.gray[200])};
	border-radius: 8px;
	padding: 16px;
	transition: all 0.2s ease;
	min-height: 140px;
	display: flex;
	flex-direction: column;
	gap: 2px;
	position: relative;

	&:hover {
		border-color: ${({ theme, $selected }) => ($selected ? theme.blue[500] : theme.gray[400])};
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
	}
`;

export const TokenName = styled.div`
	margin-top: 12px;
	font-size: 14px;
	font-weight: 600;
	color: ${({ theme }) => theme.gray[1000]};
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono',
		'Courier New', monospace;
`;

export const TokenCssVariable = styled.div`
	font-size: 12px;
	color: ${({ theme }) => theme.gray[600]};
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono',
		'Courier New', monospace;
`;

export const TokenTooltip = styled.div`
	position: absolute;
	top: 100%;
	left: 50%;
	transform: translateX(-50%) translateY(8px);
	background-color: ${({ theme }) => theme.gray[900]};
	color: ${({ theme }) => theme.gray[0]};
	padding: 6px 10px;
	border-radius: 6px;
	font-size: 12px;
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono',
		'Courier New', monospace;
	white-space: nowrap;
	pointer-events: none;
	opacity: 0;
	transition: opacity 0.2s ease;
	z-index: 10;

	${TokenCard}:hover & {
		opacity: 1;
	}

	// Arrow pointing up
	&::after {
		content: '';
		position: absolute;
		bottom: 100%;
		left: 50%;
		transform: translateX(-50%);
		border: 6px solid transparent;
		border-bottom-color: ${({ theme }) => theme.gray[900]};
	}
`;

export const SpacingLayout = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	gap: 0;
`;

export const FixedBox = styled.div`
	width: 60px;
	height: 60px;
	background-color: ${({ theme }) => theme.gray[400]};
	border-radius: 4px;
	flex-shrink: 0;
`;

export const SpacingBoxLeft = styled.div<{ spacing: number }>`
	width: ${({ spacing }) => Math.min(Math.max(spacing, 4), 80)}px;
	height: 60px;
	background-color: ${({ theme }) => theme.gray[200]};
	border-radius: 4px;
	border-top-right-radius: 0;
	border-bottom-right-radius: 0;
	flex-shrink: 0;
`;

export const SpacingBoxRight = styled.div<{ spacing: number }>`
	width: ${({ spacing }) => Math.min(Math.max(spacing, 4), 80)}px;
	height: 60px;
	background-color: ${({ theme }) => theme.gray[200]};
	border-radius: 4px;
	border-top-left-radius: 0;
	border-bottom-left-radius: 0;
	flex-shrink: 0;
`;

export const TokenGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
	gap: 24px;
	width: 100%;
`;
